import { inject, Injectable } from '@angular/core';
import { LanguageService } from '@catch/services';
import { TranslocoService } from '@jsverse/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import Fuse from 'fuse.js';
import { BehaviorSubject, Observable } from 'rxjs';
import { SearchAdapter } from '../adapters/search.adapter';
import { AppState } from '../app.reducer';
import { CompanyUserRole } from '../enums/company-user.role.enum';
import { SearchResultTypes } from '../enums/search-result-types';
import { hasPermission } from '../store/auth/auth.reducer';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SearchService {
  encodedFunctionFunctionSearchResultObjects: Array<EncodedFunctionSearchResultObject> = [];
  lastQuery: string;
  jobsLoading: boolean = false;
  applicationsLoading: boolean = false;
  functionsLoading: boolean = false;

  jobResults = new BehaviorSubject<JobSearchResult[]>([]);
  applicationResults = new BehaviorSubject<ApplicationSearchResult[]>([]);
  viewResults = new BehaviorSubject<FunctionSearchResult[]>([]);

  private translateService = inject(TranslocoService);
  private languageService = inject(LanguageService);
  private searchAdapter = inject(SearchAdapter);
  private store = inject(Store<AppState>);

  constructor() {
    this.languageService.languageChange$.pipe(untilDestroyed(this)).subscribe({
      next: () => {
        this.encodeFunctionSearch();
      },
    });
  }

  functionSearch(query: string, limit?: number): Array<FunctionSearchResult> {
    let results: Array<FunctionSearchResult> = [];
    const getNextPath = (pathObjects: Array<{ label: string; path?: string }>): string => {
      let result = null;
      pathObjects.forEach((pathObject) => {
        if (pathObject.path) {
          result = pathObject.path;
          return;
        }
      });
      return result;
    };
    const getPathLabels = (pathObjects: Array<{ label: string; path?: string }>): Array<string> => {
      const result: Array<string> = [];
      pathObjects.forEach((pathObject) => {
        result.push(pathObject.label);
      });
      return result;
    };
    this.encodedFunctionFunctionSearchResultObjects.forEach((encodedFunctionFunctionSearchResultObject) => {
      const searchResult = {
        score: undefined,
        label: undefined,
        pathLabels: [],
        path: undefined,
      };

      encodedFunctionFunctionSearchResultObject.paths.forEach((path, pathIndex) => {
        const fuse = new Fuse([path.label], { threshold: 0.3 });
        const result = fuse.search(query.toLowerCase());
        let score = result.length > 0 ? 1 - result[0].score : 0;
        if (path.label.toLowerCase().includes(query.toLowerCase())) {
          score = 0.75 + score / 4.0;
        }
        if (searchResult.score === undefined || score > searchResult.score) {
          searchResult.score = score;
          searchResult.path = getNextPath(encodedFunctionFunctionSearchResultObject.paths);
          searchResult.label = path.label;
          if (pathIndex === 0) {
            searchResult.pathLabels = getPathLabels(encodedFunctionFunctionSearchResultObject.paths.slice(1, 2));
          }
          if (pathIndex === 1) {
            searchResult.pathLabels = getPathLabels(encodedFunctionFunctionSearchResultObject.paths.slice(0, 1));
          }
        }
      });
      encodedFunctionFunctionSearchResultObject.synonyms.forEach((synonym) => {
        const fuse = new Fuse([synonym], { threshold: 0.3 });
        const result = fuse.search(query.toLowerCase());
        let score = result.length > 0 ? 1 - result[0].score : 0;
        if (synonym.toLowerCase().includes(query.toLowerCase())) {
          score = 0.74 + score / 4.0;
        }
        if (searchResult.score === undefined || score > searchResult.score) {
          searchResult.score = score;
          searchResult.path = getNextPath([...encodedFunctionFunctionSearchResultObject.paths].reverse());
          searchResult.label =
            encodedFunctionFunctionSearchResultObject.paths[
              encodedFunctionFunctionSearchResultObject.paths.length - 1
            ].label;
          searchResult.pathLabels = getPathLabels(
            encodedFunctionFunctionSearchResultObject.paths.slice(
              0,
              encodedFunctionFunctionSearchResultObject.paths.length - 1
            )
          );
        }
      });
      this.store
        .select(hasPermission(CompanyUserRole.ADMIN))
        .pipe(untilDestroyed(this))
        .subscribe((hasPermission) => {
          if (
            !results.find((result) => result.path === searchResult.path) &&
            ((encodedFunctionFunctionSearchResultObject.admin && hasPermission) ||
              !encodedFunctionFunctionSearchResultObject.admin)
          ) {
            results.push(searchResult);
          }
        });
    });

    const resultsAverageScore = results.reduce((a, b) => a + b.score, 0) / results.length;

    results = results
      .filter((result) => result.score > resultsAverageScore)
      .sort((a, b) => {
        if (a.score < b.score) return 1;
        if (a.score > b.score) return -1;
        return 0;
      });
    if (limit) {
      results = results.slice(0, limit);
    }

    return results;
  }

  encodeFunctionSearch() {
    this.encodedFunctionFunctionSearchResultObjects = [];
    this.store
      .select(hasPermission(CompanyUserRole.ADMIN))
      .pipe(untilDestroyed(this))
      .subscribe((hasPermission) => {
        searchResultObjects.forEach((searchResultObject) => {
          const encodedFunctionSearchResultObject: EncodedFunctionSearchResultObject = {
            paths: [],
            synonyms: [],
            admin: searchResultObject.admin,
          };
          searchResultObject.paths.forEach((path) => {
            const encodedPath = path;
            this.translateService
              .selectTranslate(path.label)
              .pipe(untilDestroyed(this))
              .subscribe({
                next: (translation) => {
                  encodedPath.label = translation;
                  encodedFunctionSearchResultObject.paths.push(encodedPath);
                },
              });
          });
          this.translateService
            .selectTranslate(searchResultObject.synonyms)
            .pipe(untilDestroyed(this))
            .subscribe({
              next: (translatedSynonyms) => {
                translatedSynonyms.split(';').forEach((synonymSplit) => {
                  const matches = synonymSplit.match(/(.*)\[(.*)\](.*)/);
                  if (matches?.length == 4) {
                    matches[2].split(',').forEach((matchSplit) => {
                      encodedFunctionSearchResultObject.synonyms.push(`${matches[1]} ${matchSplit} ${matches[3]}`);
                    });
                  } else {
                    encodedFunctionSearchResultObject.synonyms.push(synonymSplit);
                  }
                });
              },
            });
          this.encodedFunctionFunctionSearchResultObjects.push(encodedFunctionSearchResultObject);
        });
      });
  }

  getBestResults(query: string) {
    if (query.length < 3) return;
    this.lastQuery = query;
    this.getJobsResults(query, 3);
    this.getApplicationsResults(query, 3);
    this.getFunctionResults(query, 3);
  }

  getPreviewResults(query: string) {
    return new Observable((subscriber) => {
      if (query.length < 3) {
        subscriber.next([]);
        return;
      }
      this.searchAdapter
        .getResults(query, 6)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (result) => {
            const functions = this.functionSearch(query, 1);

            let resultArray = result.applications
              .map((item) => ({
                ...item,
                type: SearchResultTypes.APPLICATION,
              }))
              .concat(
                result.jobs.map((item) => ({ ...item, type: SearchResultTypes.JOB })),
                functions.map((item) => ({ ...item, type: SearchResultTypes.VIEW }))
              );
            resultArray = resultArray.sort((a, b) => b.score - a.score);
            subscriber.next(resultArray);
          },
        });
    });
  }

  getJobsResults(query: string, limit?: number) {
    if (query.length < 3) return;
    this.lastQuery = query;
    this.jobsLoading = true;

    this.searchAdapter
      .getJobResults(query, limit)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result: any) => {
          this.jobsLoading = false;
          this.jobResults.next(result.jobs);
        },
      });
  }

  getApplicationsResults(query: string, limit?: number) {
    if (query.length < 3) return;
    this.lastQuery = query;
    this.applicationsLoading = true;

    this.searchAdapter
      .getApplicationResults(query, limit)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result: any) => {
          this.applicationsLoading = false;
          this.applicationResults.next(result.applications);
        },
      });
  }

  getFunctionResults(query: string, limit?: number) {
    if (query.length < 3) return;
    this.lastQuery = query;
    this.functionsLoading = true;

    this.viewResults.next(this.functionSearch(query, limit));
    this.functionsLoading = false;
  }

  applicationTrackBy(index, application: ApplicationSearchResult) {
    return application.id;
  }

  jobTrackBy(index, job: JobSearchResult) {
    return job.id;
  }
}

export interface JobSearchResult {
  title: string;
  id: number;
  creationTimestamp: string;
  state: number;
  applicationCount: number;
  score: number;
}

import { AcademicTitle } from '../enums/academic-title.enum';
import { Gender } from '../enums/gender.enum';

export interface ApplicationSearchResult {
  title: Gender;
  academicTitle: AcademicTitle | null;
  firstName: string;
  lastName: string;
  id: number;
  jobID: number;
  jobTitle: string;
  score: number;
}

export interface FunctionSearchResult {
  score: number;
  label: string;
  pathLabels: Array<string>;
  path: string;
}

export interface FunctionSearchResultObject {
  paths: Array<{
    label: string;
    path?: string;
  }>;
  synonyms: string;
  admin: boolean;
}

export interface EncodedFunctionSearchResultObject {
  paths: Array<{
    label: string;
    path?: string;
  }>;
  synonyms: Array<string>;
  admin: boolean;
}

export const searchResultObjects: Array<FunctionSearchResultObject> = [
  {
    //jobs archive
    paths: [
      {
        label: 'company-tool.views.jobs.archive.title.main',
        path: '/jobs/archive',
      },
      {
        label: 'company-tool.views.jobs.archive.title.sub',
      },
    ],
    synonyms: 'company-tool.view-search.jobs.archive.archive-synonyms',
    admin: true,
  },
  {
    //jobs list
    paths: [
      {
        label: 'company-tool.views.jobs.main',
      },
      {
        label: 'company-tool.views.jobs.list.title.sub',
        path: '/jobs',
      },
    ],
    synonyms: 'company-tool.view-search.jobs.list.jobs-list-synonyms',
    admin: false,
  },
  {
    //jobs posting
    paths: [
      {
        label: 'company-tool.views.jobs.main',
      },
      {
        label: 'company-tool.views.jobs.posting.title.sub',
        path: '/jobs/new',
      },
    ],
    synonyms: 'company-tool.view-search.jobs.posting.job-posting-synonyms',
    admin: true,
  },
  {
    //company settings account data
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.account.title.sub',
        path: '/settings/company/account',
      },
      {
        label: 'company-tool.views.settings.company.account.sections.contact.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.account.account-data-synonyms',
    admin: true,
  },
  {
    //company settings account data
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.account.title.sub',
        path: '/settings/company/account',
      },
      {
        label: 'company-tool.views.settings.company.account.sections.account-actions.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.account.manage-account-synonyms',
    admin: true,
  },
  {
    //company settings account data
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.account.title.sub',
        path: '/settings/company/account',
      },
      {
        label: 'company-tool.views.settings.company.account.sections.more-information.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.account.more-information-synonyms',
    admin: true,
  },
  {
    //company settings application process
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.application-process.title.sub',
        path: '/settings/company/process',
      },
      {
        label: 'company-tool.views.settings.company.application-process.sections.default-process.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.application-process.application-process-synonyms',
    admin: true,
  },
  {
    //company settings design logo
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.design.title.sub',
        path: '/settings/company/design',
      },
      {
        label: 'company-tool.views.settings.company.design.sections.logo.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.design.logo-synonyms',
    admin: true,
  },
  {
    //company settings design icon
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.design.title.sub',
        path: '/settings/company/design',
      },
      {
        label: 'company-tool.views.settings.company.design.sections.icon.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.design.favicon-synonyms',
    admin: true,
  },
  {
    //company settings design jobboard
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.design.title.sub',
        path: '/settings/company/design',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.design.colors-synonyms',
    admin: true,
  },
  {
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.workflows.title',
        path: '/settings/company/workflows',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.workflows.workflows-synonyms',
    admin: true,
  },
  {
    //company settings email templates
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.emails.title.sub',
        path: '/settings/company/emails',
      },
      {
        label: 'company-tool.views.settings.company.emails.sections.edit-templates.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.emails.emails-synonyms',
    admin: true,
  },
  {
    //company settings inbox
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.emails.title.sub',
        path: '/settings/company/emails',
      },
      {
        label: 'company-tool.views.settings.company.emails.sections.inbox.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.emails.inbox-synonyms',
    admin: true,
  },
  {
    //company settings facilities
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.facilities.title.sub',
        path: '/settings/company/facilities',
      },
      {
        label: 'company-tool.views.settings.company.facilities.sections.list.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.facilities.facilities-synonyms',
    admin: true,
  },
  {
    //company settings integration
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.integrations.title.sub',
        path: '/settings/company/integration',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.integration.integration-synonyms',
    admin: true,
  },
  {
    //company settings jobboard order
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.jobboard.title.sub',
        path: '/settings/company/jobboard',
      },
      {
        label: 'company-tool.views.settings.company.jobboard.sections.order.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.jobboard.order-synonyms',
    admin: true,
  },
  {
    //company settings jobboard link
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.jobboard.title.sub',
        path: '/settings/company/jobboard',
      },
      {
        label: 'company-tool.views.settings.company.jobboard.sections.link.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.jobboard.link-synonyms',
    admin: true,
  },
  {
    //company settings privacy adv
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.jobboard.title.sub',
        path: '/settings/company/privacy',
      },
      {
        label: 'company-tool.views.settings.company.privacy.sections.adv.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.privacy.avv-synonyms',
    admin: true,
  },
  {
    //company settings privacy
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.privacy.title.sub',
        path: '/settings/company/privacy',
      },
      {
        label: 'company-tool.views.settings.company.privacy.sections.privacy-policy.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.privacy.privacy-synonyms',
    admin: true,
  },
  {
    //company settings users
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.users.title.sub',
        path: '/settings/company/users',
      },
      {
        label: 'company-tool.views.settings.company.users.sections.users.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.users.users-synonyms',
    admin: true,
  },
  {
    //company settings users new
    paths: [
      {
        label: 'company-tool.views.settings.company.main',
      },
      {
        label: 'company-tool.views.settings.company.users.title.sub',
        path: '/settings/company/users',
      },
      {
        label: 'company-tool.views.settings.company.users.sections.add-user.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.company.users.new-user-synonyms',
    admin: true,
  },
  {
    //profile settings account
    paths: [
      {
        label: 'company-tool.views.settings.profile.main',
      },
      {
        label: 'company-tool.views.settings.profile.account.title.sub',
        path: '/settings/profile/account',
      },
      {
        label: 'company-tool.views.settings.profile.account.sections.personal-data.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.profile.account.personal-data-synonyms',
    admin: false,
  },
  {
    //profile settings password
    paths: [
      {
        label: 'company-tool.views.settings.profile.main',
      },
      {
        label: 'company-tool.views.settings.profile.account.title.sub',
        path: '/settings/profile/account',
      },
      {
        label: 'company-tool.views.settings.profile.account.sections.password.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.profile.account.password-synonyms',
    admin: false,
  },
  {
    //profile settings notifications
    paths: [
      {
        label: 'company-tool.views.settings.profile.main',
      },
      {
        label: 'company-tool.views.settings.profile.notifications.title.sub',
        path: '/settings/profile/notifications',
      },
    ],
    synonyms: 'company-tool.view-search.settings.profile.notifications.notifications-synonyms',
    admin: false,
  },
  {
    //profile settings signatures
    paths: [
      {
        label: 'company-tool.views.settings.profile.main',
      },
      {
        label: 'company-tool.views.settings.profile.signatures.title.sub',
        path: '/settings/profile/signatures',
      },
      {
        label: 'company-tool.views.settings.profile.signatures.sections.manage-signatures.headline',
      },
    ],
    synonyms: 'company-tool.view-search.settings.profile.signatures.signatures-synonyms',
    admin: false,
  },
  {
    //talentpool
    paths: [
      {
        label: 'company-tool.views.talents.main',
      },
      {
        label: 'company-tool.views.talents.sub-1',
        path: '/talents',
      },
    ],
    synonyms: 'company-tool.view-search.talents.talents-synonyms',
    admin: true,
  },
  //*/
];
